// Dashboard.js
import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactEditList from "react-edit-list";
import { getDocs, collection, query, addDoc, orderBy, doc, deleteDoc, setDoc } from 'firebase/firestore/lite';
import {db} from './../firebase';
import { DASHBOARD_CRUD, notifyDashboardCrudError, failDashboardValidation, isBlank } from '../utils/dashboardCrud';

// Using native HTML5 drag and drop
import { useDragAndDrop } from './useDragAndDrop';

export const SubLinesList = () => {

  const DOCUMENT_NAME = 'sub-research-lines';

  const TITLE = 'Sub-líneas de Investigación';
  
  const [data, setData] = useState([]);
  const dataRef = useRef([]);
  const [researchLines, setResearchLines] = useState([]);

  const navigate = useNavigate();

  const goToDashboard = async () => {
    navigate('/dashboard');
  };

  // Keep dataRef in sync with data state
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const ref = useRef(null);
  
  // Use drag and drop hook
  const { initDragAndDrop, isSaving } = useDragAndDrop(data, setData, dataRef, DOCUMENT_NAME, TITLE, ref);

  // Cargar líneas de investigación para el dropdown
  const fetchResearchLines = async () => {
    try {
      const q = query(collection(db, 'research-lines'), orderBy('order'));
      const querySnapshot = await getDocs(q);
      const newData = querySnapshot.docs
        .map((doc) => ({ ...doc.data(), id:doc.id }));
      setResearchLines(newData);
    } catch (error) {
      notifyDashboardCrudError({ operation: DASHBOARD_CRUD.LOAD, entityName: 'Líneas de investigación', error });
      setResearchLines([]);
    }
  };

  const fetchData = async () => {
    try {
      const q = query(collection(db, DOCUMENT_NAME), orderBy('orden'));
      const querySnapshot = await getDocs(q);
      const newData = querySnapshot.docs
        .map((doc) => ({ ...doc.data(), id:doc.id }));
      setData(newData);
      dataRef.current = newData;
      
      // Initialize drag and drop after data is loaded
      setTimeout(() => {
        initDragAndDrop();
      }, 1000);
    } catch (error) {
      notifyDashboardCrudError({ operation: DASHBOARD_CRUD.LOAD, entityName: TITLE, error });
      setData([]);
    }
  }

  useEffect(()=>{
    fetchResearchLines();
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Crear schema dinámicamente basado en researchLines
  const createSchema = () => {
    // Usar ID en lugar de nombre para la relación
    const lineaOptions = researchLines.length > 0 
      ? researchLines.map(line => ({ value: line.id, name: line.name }))
      : [{ value: "", name: "Sin líneas disponibles" }];
    
    return [
      { name: "id", type: "id" },
      { name: "titulo", type: "string" },
      { name: "linea_investigacion", type: lineaOptions },
      { name: "orden", type: "number" },
      { name: "enabled", type: [
          { value: true, name: "Si" },
          { value: false, name: "No" }
        ] 
      }
    ];
  };

  const headers = {
    "titulo"              : "Título",
    "linea_investigacion" : "Línea de Investigación",
    "orden"               : "Orden",
    "enabled"             : "Mostrar"
  };

  return (
    <div className="container-fluid mt-1">
        <div className="card shadow">
            <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                <button className="btn btn-danger" onClick={goToDashboard} >Regresar</button>
                <h3 className="mb-0">Listado de {TITLE}</h3>
                {isSaving && (
                  <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm text-light me-2" role="status">
                      <span className="visually-hidden">Guardando...</span>
                    </div>
                    <span className="text-light">Guardando cambios...</span>
                  </div>
                )}
            </div>
            <div className="card-body">
                <div className="table-responsive">
                    <React.Fragment>
                      <div className="m-3 fw-bold">
                        Se pueden reordenar las {TITLE.toLowerCase()} moviéndolas de orden en la lista
                      </div>
                      <ReactEditList
                        key={researchLines.length} // Force re-render when researchLines change
                        schema={createSchema()}
                        headers={headers}
                        onLoad={() => data}
                        ref={ref}
                        render={{
                          linea_investigacion: function LineaRenderer(props) {
                            // props.value ahora es un ID, buscar el nombre
                            const linea = researchLines.find(l => l.id === props.value);
                            if (linea) {
                              return <span>{linea.name}</span>;
                            }
                            // Fallback: mostrar el valor directamente (compatibilidad con datos antiguos)
                            return <span>{props.value}</span>;
                          }
                        }}
                        onUpdate={async (item) => {
                          // Call your API here
                          if (isBlank(item.titulo)) {
                            return failDashboardValidation({
                              operation: DASHBOARD_CRUD.UPDATE,
                              entityName: TITLE,
                              missingFields: ['Título'],
                              message: 'Debes capturar el título de la sub-línea.',
                            });
                          }
                          if (!item.linea_investigacion) {
                            return failDashboardValidation({
                              operation: DASHBOARD_CRUD.UPDATE,
                              entityName: TITLE,
                              missingFields: ['Línea de investigación'],
                              message: 'Selecciona una línea de investigación.',
                            });
                          }
                          if (item.orden === undefined || item.orden === null) {
                            item.orden = item.order || 0;
                          }
                          // Mantener ambos campos sincronizados
                          item.order = item.orden;
                          item.enabled = item.enabled === 'false' || item.enabled === false ? false : true;
                          try {
                            await setDoc(doc(db, DOCUMENT_NAME, item.id), item);
                          } catch (error) {
                            notifyDashboardCrudError({ operation: DASHBOARD_CRUD.UPDATE, entityName: TITLE, error });
                            return false;
                          }
                        }}
                        onDelete={async (item) => {
                          if (!window.confirm("¿Estás seguro de que quieres eliminar esta sub-línea?")) return false;
                          // Call your API here
                          try {
                            await deleteDoc(doc(db, DOCUMENT_NAME, item.id));
                          } catch (error) {
                            notifyDashboardCrudError({ operation: DASHBOARD_CRUD.DELETE, entityName: TITLE, error });
                            return false;
                          }
                        }}
                        onInsert={async (item) => {
                          // Call your API here
                          if (isBlank(item.titulo)) {
                            return failDashboardValidation({
                              operation: DASHBOARD_CRUD.CREATE,
                              entityName: TITLE,
                              missingFields: ['Título'],
                              message: 'Debes capturar el título de la sub-línea.',
                            });
                          }
                          if (!item.linea_investigacion) {
                            item.linea_investigacion = researchLines[0].id;
                            //return false;
                          }
                          if (item.orden === undefined || item.orden === null) {
                            item.orden = data.length;
                          }
                          // Mantener ambos campos sincronizados
                          item.order = item.orden;
                          item.enabled = item.enabled === 'false' || item.enabled === false ? false : true;
                          item.createdDate = new Date();
                          item.updatedDate = new Date();
                          try {
                            const docRef = await addDoc(collection(db, DOCUMENT_NAME), item);
                            item.id = docRef.id;
                          } catch (error) {
                            notifyDashboardCrudError({ operation: DASHBOARD_CRUD.CREATE, entityName: TITLE, error });
                            return false;
                          }
                        }}
                        className="table table-light table-fixed align-middle"
                        headClassName="table-primary"
                        inputClassName="w-100"
                        thClassName={{
                          titulo: "col-4",
                          linea_investigacion: "col-4",
                          orden: "col-2",
                          enabled: "col-2"
                        }}
                        bodyClassName="sortable-items"
                        btnValidateClassName="btn btn-success p-0 m-0"
                        btnDeleteClassName="btn btn-danger py-0 px-1 m-0 mx-1"
                        btnCancelClassName="btn btn-secondary py-0 px-1 m-0 mx-1"
                        rowClassName="draggable"
                        insertClassName="not-draggable"
                        filler={<React.Fragment>&#8230;</React.Fragment>}
                      />
                    </React.Fragment>
                </div>
            </div>
        </div>
    </div>
  );
}

